import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DEFAULTVALUE from '@/app/constants/defaultValue';
import { DetailsTravelItem } from '@/app/models/detailsTravel';
import { PersonResponse } from '@/app/models/person';
import { useUpdateDetailsTravelMutation } from '@/app/services/detailsTravelApi';
import { useSearchPersonQuery } from '@/app/services/personApi';
import { formatDateForBackend } from '@/app/utils/dateUtils';
import formatterMonetaryValue, { formatCurrency } from '@/app/utils/numberUtils';
import DrawerButtons from '@/components/common/DrawerButtons';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { usePeriod } from '@/context/PeriodContext';
import useFormattedCurrency from '@/hooks/useFormattedCurrency';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import FormField from '../baseForms/FormField';
import FormFieldLong from '../baseForms/FormFieldLong';
import LaunchDetailsBox from '../baseForms/info/LaunchDetailsBox';

interface NewEditTravelDetailsProps {
  selectItem: DetailsTravelItem;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditTravelDetails(props: NewEditTravelDetailsProps) {
  const { t, i18n } = useTranslation(['travel', 'common']);

  const theme = useTheme();
  const { selectItem, setDrawerOpen } = props;
  const [launchComplement] = useState<string>(selectItem?.complement ? selectItem.complement : '');

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const { value: totalValue, setValue: setTotalValue } = useFormattedCurrency(selectItem?.value);

  const [plannedInitDate, setPlannedInitDate] = useState<string>(
    selectItem?.initDate ? dayjs(selectItem?.initDate).format('DD/MM/YYYY') : '',
  );
  const [plannedEndDate, setPlannedEndDate] = useState<string>(
    selectItem?.endDate ? dayjs(selectItem?.endDate).format('DD/MM/YYYY') : '',
  );

  const [objecives, setObjecives] = useState<string>(selectItem?.objective ? selectItem?.objective : '');
  const [local, setLocal] = useState<string>(selectItem?.local ? selectItem?.local : '');
  const [justification, setJustification] = useState<string>(selectItem?.justify ? selectItem.justify : '');

  const [originPerson, setOriginPerson] = useState<PersonResponse | null>(selectItem?.person ?? null);
  const [originPersonSearch, setOriginPersonSearch] = useState('');
  const { data: originPersons, isFetching: isLoadingOrigin } = useSearchPersonQuery(
    {
      searchTerm: originPersonSearch,
      page: 0,
      size: 10,
      sortKey: '',
    },
    {
      skip: originPersonSearch.length < 1,
    },
  );

  const { selectedPeriod } = usePeriod();

  const [update] = useUpdateDetailsTravelMutation();
  const parsePersonId = originPerson?.id ? String(originPerson.id) : String(selectItem.person?.id ?? '');

  const handleSubmit = async () => {
    setDrawerOpen(false);
    await toast.promise(
      update({
        id: selectItem.id,
        personId: parsePersonId,
        period: selectedPeriod?.date as string,
        objective: objecives,
        initDate: formatDateForBackend(plannedInitDate),
        endDate: formatDateForBackend(plannedEndDate),
        justify: justification,
        local,
        value: parseFloat(totalValue.replace('R$', '').replace('.', '').replace(',', '.')) || DEFAULTVALUE,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('updated.successfully') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const hasNegative = val.includes('-');
    const cleaned = val.replace(/[^0-9]/g, '');
    const raw = hasNegative ? `-${cleaned}` : cleaned;
    setTotalValue(formatCurrency(raw));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('common:edit.detail')} />

        <LaunchDetailsBox
          totalValue={formatterMonetaryValue(selectItem?.value || null)}
          launchComplement={launchComplement}
          complementDisabled
        />

        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        />
        <Box sx={{ display: 'block', mb: 2 }}>
          <Box sx={{ mb: 2, mt: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
            >
              <Typography variant="fieldHeader" width={theme.spacing(20.6)}>
                {t('common:collaborator')}
              </Typography>
              <GenericAutoComplete
                value={originPerson}
                onChange={(value) => {
                  setOriginPerson(value);
                }}
                equalityTest={(option, value) => option?.personBadge === value?.personBadge}
                getOptionLabel={(option) => (option ? `${option.personBadge ?? ''} - ${option.name ?? ''}` : '')}
                onSearch={(value) => {
                  setOriginPersonSearch(value);
                }}
                options={originPersonSearch !== '' && originPersons?.content ? originPersons.content : []}
                placeholder={t('common:type.to.search') as string}
                loading={isLoadingOrigin}
                sx={{
                  width: theme.spacing(80),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Stack>
          </Box>

          <FormField label={t('common:total.value') as string} value={totalValue} onChange={handleValueChange} />

          <Box sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="fieldHeader" width={theme.spacing(17)}>
                  {t('common:start')}
                </Typography>
                <Stack direction="row" sx={{ width: '28%' }}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={plannedInitDate ? dayjs(plannedInitDate, 'DD/MM/YYYY') : null}
                    onChange={(newValue: Dayjs | null) =>
                      setPlannedInitDate(newValue ? newValue.format('DD/MM/YYYY') : '')
                    }
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                      },
                    }}
                    slots={{ openPickerIcon: () => null }}
                  />
                </Stack>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Typography variant="fieldHeader" width={theme.spacing(17)}>
                  {t('common:end')}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '28%' }}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={plannedEndDate ? dayjs(plannedEndDate, 'DD/MM/YYYY') : null}
                    onChange={(newValue: Dayjs | null) =>
                      setPlannedEndDate(newValue ? newValue.format('DD/MM/YYYY') : '')
                    }
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                      },
                    }}
                    slots={{ openPickerIcon: () => null }}
                  />
                </Stack>
              </Box>
            </LocalizationProvider>
          </Box>

          <FormField label={t('local') as string} value={local as string} onChange={(e) => setLocal(e.target.value)} />

          <FormFieldLong
            label={t('common:objective')}
            value={objecives as string}
            onChange={(e) => setObjecives(e.target.value)}
          />

          <FormFieldLong
            label={t('common:justification')}
            value={justification as string}
            onChange={(e) => setJustification(e.target.value)}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={!plannedEndDate} />
      </Box>
    </Box>
  );
}
